<template>
  <RouterLink :to="`/product/${item.modelId}/${item.sku}`">
    <wa-card class="productCard">
      <div slot="media" style="position: relative">
        <SanityImage
          :src="`${item.image}?w=300&h=230&fit=fill&crop=center&dpr=2&bg=F6F6F6&q=70`"
          :alt="item.title.de"
          loading="eager"
          width="300"
          :height="item.type === 'accessory' ? 300 : 300"
        />
        <div class="productCard__attributes">
          <div v-if="item.tags" class="tags">
            <ProductTags :item="item" tags-list-width="auto" />
          </div>
          <div v-if="wishlistFeatureEnabled" class="fav">
            <WishlistButton :item="item" />
          </div>
          <div
            v-if="item.collaboration || (item.collection && item.collection.includes('Limited'))"
            class="collab"
          >
            <template v-if="item.collection && item.collection.includes('Limited')"
              >Limited Collection</template
            >
            <template v-else>{{ item.collaboration }}</template>
          </div>
        </div>
      </div>
      <div class="productCard__layout" style="margin-bottom: 4px">
        <div class="productCard__leftSide">
          <div class="productCard__title">{{ item.title.de }}</div>
        </div>

        <div class="productCard__rightSide">
          <CurrencyFormatter
            v-if="item.price.default[deviceStore.settings.country]?.price > 0"
            :value="item.price.default[deviceStore.settings.country]?.price"
            :currency="deviceStore.settings.currency"
            :class="
              item.price.default[deviceStore.settings.country]?.compareAt !==
              item.price.default[deviceStore.settings.country]?.price
                ? 'red'
                : ''
            "
          />
        </div>
      </div>

      <div
        class="productCard__layout"
        :class="item.type === 'accessory' ? 'productCard__layout--accessory' : ''"
      >
        <div class="productCard__leftSide">
          <div class="text-color-neutral">
            <template v-if="item.colour"
              >{{ item.colour.name }} {{ item.finish ? capitalizeFirstLetter(item.finish) : ''
              }}<span v-if="wirecoreLabel" class="wirecore"> / {{ wirecoreLabel }}</span
              ><br
            /></template>
            <template v-if="item.size && item.type !== 'accessory'"
              >{{ item.size.sizeGroup }} ({{ item.size.frameWidth }}mm)</template
            >
          </div>
        </div>

        <div class="productCard__rightSide">
          <CurrencyFormatter
            v-if="
              item.price.default[deviceStore.settings.country]?.compareAt !==
              item.price.default[deviceStore.settings.country]?.price
            "
            :value="item.price.default[deviceStore.settings.country]?.compareAt"
            :currency="deviceStore.settings.currency"
            class="strikethroug"
          />
          <div class="productCard__stockAndAction">
            <StockStatusIndicator
              v-if="item.stock && !item.doNotTrack"
              :status-color="getStockColour(item.stock[deviceStore.settings.warehouse])"
              class="productCard__stock"
            />
            <wa-button
              v-if="addToCartEnabled"
              variant="neutral"
              appearance="outlined"
              size="s"
              class="quickAddToCart"
              :loading="addToCartLoading"
              @click.prevent="addAccessoryToCart(item)"
              ><PhShoppingBag :size="18"
            /></wa-button>
          </div>
        </div>
      </div>
    </wa-card>
  </RouterLink>
</template>

<script setup>
import SanityImage from '@/components/shared/SanityImage.vue'
import { useCart } from '@/composables/useCart.js'
import { useNotification } from '@/composables/useNotification.js'
import { useWishlists } from '@/composables/useWishlists.js'
import { useCustomerStore } from '@/stores/customer'
import { useDeviceStore } from '@/stores/device'
import { useI18n } from 'vue-i18n'

import CurrencyFormatter from '@/components/shared/CurrencyFormatter/CurrencyFormatter.vue'
import StockStatusIndicator from '@/components/shared/StockStatusIndicator/StockStatusIndicator.vue'
import WishlistButton from '@/components/Wishlist/WishlistButton.vue'
import { getStockColour } from '@/utils/stock.js'
import { getWirecoreLabel } from '@/utils/product.js'
import { PhShoppingBag } from '@phosphor-icons/vue'
import '@awesome.me/webawesome/dist/components/button/button.js'
import '@awesome.me/webawesome/dist/components/card/card.js'
import '@awesome.me/webawesome/dist/components/format-number/format-number.js'
import '@awesome.me/webawesome/dist/components/tag/tag.js'
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import ProductTags from './ProductTags.vue'

const deviceStore = useDeviceStore()
const customerStore = useCustomerStore()
const { wishlistIsEnabled } = useWishlists()
const { createCart, addLineItem, cartUpdate } = useCart()
const addToCartLoading = ref(false)
const wishlistFeatureEnabled = computed(() => wishlistIsEnabled())

const notification = useNotification()
const { t } = useI18n()

const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  },
  index: {
    type: Number,
    default: 0
  }
})

function capitalizeFirstLetter(string = '') {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
}

const wirecoreLabel = computed(() => getWirecoreLabel(props.item))

const addToCartEnabled = computed(() => {
  if (
    !props.item.stock ||
    !props.item.stock[deviceStore.settings.warehouse] ||
    props.item.stock[deviceStore.settings.warehouse]?.status !== 1
  ) {
    return false
  }

  if (
    props.item.type === 'accessory' &&
    ['the-cleaning-cloth', 'the-cleaning-kit'].includes(props.item.slug)
  ) {
    return true
  }

  if (props.item.type !== 'accessory' || props.item.sku === 'giftcard') {
    return false
  }

  if (props.item.productStatus === 'COMING_SOON') {
    return false
  }

  if (props.item.doNotTrack) {
    return true
  }

  return true
})

async function addAccessoryToCart(item) {
  addToCartLoading.value = true
  if (!customerStore.customer?.currentCart) {
    await createCart()
  }

  const mainResponse = await addLineItem({
    sku_code: item.sku,
    quantity: 1,
    metadata: {
      type: 'accessory',
      ...(!props.item.doNotTrack && {
        lead_time_days: item.stock[deviceStore.settings.warehouse].leadTimeDays
      }),
      origin_stock_location: deviceStore.settings.warehouse,
      immediate_handover: true
    }
  })

  if (mainResponse) {
    await cartUpdate()
    addToCartLoading.value = false

    notification.showNotification({
      message: `${props.item.title[deviceStore.settings.language]} ${t(
        'configurator.infoMessages.productWasAddedToCart'
      )}`
    })

    return
  }

  addToCartLoading.value = false
}
</script>

<style scoped>
/* the RouterLink wrapping the card: keep the card text free of the link underline/colour */
a {
  text-decoration: none;
  color: inherit;
}

.productCard {
  --spacing: 12px;
  height: 100%;
  width: 100%;
  text-decoration: none;
  color: var(--color--black);
  position: relative;
  border: 0;
  box-shadow: none;

  &::part(body) {
    padding-left: 0;
    padding-right: 0;
  }

  &::part(media) {
    border: 0;
    width: 100%;
  }

  img {
    width: 100%;
    height: auto;
  }

  &__layout {
    display: grid;
    grid-template-columns: 1fr 80px;
    gap: 0 3px;
    line-height: var(--wa-line-height-condensed);

    &--accessory {
      align-items: center;
    }
  }

  &__title {
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-weight: var(--font--bold);
  }

  &__attributes {
    position: absolute;
    inset: 10px;
    height: calc(100% - 24px);
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: 1fr 1fr;
    gap: 0 12px;
    grid-template-areas:
      'tags fav'
      'collab collab';
    line-height: 1.9;

    .collab {
      grid-area: collab;
      text-align: center;
      text-transform: uppercase;
      font-size: 10px;
      letter-spacing: 4px;
      font-weight: var(--font--bold);
      align-self: end;
    }

    .tags {
      grid-area: tags;
    }

    .fav {
      grid-area: fav;
      display: flex;
      align-items: flex-start;
      justify-content: flex-end;
      z-index: 1;
    }

    .saveFav {
      /* TODO */
      padding: var(--wa-space-s);
      margin-top: -8px;
      position: relative;

      .wishlist-count {
        position: absolute;
        top: 2px;
        right: 2px;
        background: var(--wa-color-brand-40);
        color: white;
        border-radius: 50%;
        width: 16px;
        height: 16px;
        font-size: 10px;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        border: 2px solid white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        pointer-events: none;
      }
    }

    .stock {
      grid-area: stock;
      text-align: right;
    }
  }

  &__rightSide {
    font-weight: var(--wa-font-weight-normal);
    text-align: right;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .red {
      color: red;
    }

    .strikethroug {
      text-decoration: line-through;
      color: var(--neutral-500, #969696);
    }
  }

  &__stock {
    margin-top: 4px;
  }

  &__stockAndAction {
    display: flex;
    gap: 4px;
    align-items: flex-end;
    align-items: center;
  }
}

wa-button.quickAddToCart {
  &[variant='neutral'][appearance='outlined'] {
    &::part(base) {
      height: 36px;
      width: 36px;
      padding: 0;
      border: 1px solid var(--wa-color-neutral-70);
      border-radius: var(--wa-form-control-border-radius);
      margin-left: 4px;
    }
    &::part(label) {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      color: var(--color--black);
    }
  }
}
</style>
